import React, { useState, useEffect, useRef, useCallback } from 'react';
import { GoogleMap, useJsApiLoader, Polyline, Marker } from '@react-google-maps/api';
import { getVeiculos, getHistorico } from '../services/api';

const GOOGLE_MAPS_KEY = 'AIzaSyC_ZmKM-BeaAygfsk23bBCSw5oqH8VX13g';
const mapContainerStyle = { width: '100%', height: '100%' };

function formatarData(d) {
    if (!d) return '';
    return new Date(d).toLocaleString('pt-BR');
}

function pad(n) { return String(n).padStart(2, '0'); }

function dataParaInput(d) {
    const dt = new Date(d);
    return `${dt.getFullYear()}-${pad(dt.getMonth()+1)}-${pad(dt.getDate())}T${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
}

export default function Historico({ onFechar }) {
    const [veiculos, setVeiculos] = useState([]);
    const [veiculoId, setVeiculoId] = useState('');
    const [inicio, setInicio] = useState(dataParaInput(Date.now() - 86400000));
    const [fim, setFim] = useState(dataParaInput(Date.now()));
    const [posicoes, setPosicoes] = useState([]);
    const [carregando, setCarregando] = useState(false);
    const [erro, setErro] = useState('');

    // Replay
    const [replay, setReplay] = useState(false);
    const [replayIdx, setReplayIdx] = useState(0);
    const [velocidadeReplay, setVelocidadeReplay] = useState(5);
    const intervalRef = useRef(null);
    const mapRef = useRef(null);

    const { isLoaded } = useJsApiLoader({ googleMapsApiKey: GOOGLE_MAPS_KEY });

    useEffect(() => {
        getVeiculos().then(r => setVeiculos(r.data));
    }, []);

    const buscar = async () => {
        if (!veiculoId) return setErro('Selecione um veículo');
        setErro('');
        setCarregando(true);
        setReplay(false);
        setReplayIdx(0);
        try {
            const res = await getHistorico(veiculoId, new Date(inicio).toISOString(), new Date(fim).toISOString());
            if (res.data.length === 0) setErro('Nenhuma posição encontrada no período');
            setPosicoes(res.data);
            if (res.data.length > 0 && mapRef.current) {
                const bounds = new window.google.maps.LatLngBounds();
                res.data.forEach(p => bounds.extend({ lat: p.latitude, lng: p.longitude }));
                mapRef.current.fitBounds(bounds);
            }
        } catch(e) {
            setErro('Erro ao buscar histórico');
        } finally {
            setCarregando(false);
        }
    };

    const iniciarReplay = () => {
        setReplayIdx(0);
        setReplay(true);
    };

    const pararReplay = () => {
        setReplay(false);
        clearInterval(intervalRef.current);
    };

    useEffect(() => {
        if (!replay || posicoes.length === 0) return;
        clearInterval(intervalRef.current);
        intervalRef.current = setInterval(() => {
            setReplayIdx(idx => {
                if (idx >= posicoes.length - 1) {
                    clearInterval(intervalRef.current);
                    setReplay(false);
                    return idx;
                }
                const next = idx + 1;
                if (mapRef.current) {
                    mapRef.current.panTo({ lat: posicoes[next].latitude, lng: posicoes[next].longitude });
                }
                return next;
            });
        }, Math.max(100, 1000 / velocidadeReplay));
        return () => clearInterval(intervalRef.current);
    }, [replay, velocidadeReplay, posicoes]);

    const onMapLoad = useCallback(map => { mapRef.current = map; }, []);

    const path = posicoes.map(p => ({ lat: p.latitude, lng: p.longitude }));
    const pathReplay = posicoes.slice(0, replayIdx + 1).map(p => ({ lat: p.latitude, lng: p.longitude }));
    const posAtual = posicoes[replayIdx];

    const distancia = () => {
        let total = 0;
        for (let i = 1; i < posicoes.length; i++) {
            const R = 6371;
            const dLat = (posicoes[i].latitude - posicoes[i-1].latitude) * Math.PI / 180;
            const dLng = (posicoes[i].longitude - posicoes[i-1].longitude) * Math.PI / 180;
            const a = Math.sin(dLat/2)**2 + Math.cos(posicoes[i-1].latitude * Math.PI/180) * Math.cos(posicoes[i].latitude * Math.PI/180) * Math.sin(dLng/2)**2;
            total += R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
        }
        return total.toFixed(2);
    };

    const velMax = posicoes.length > 0 ? Math.max(...posicoes.map(p => p.velocidade || 0)) : 0;
    const velMedia = posicoes.length > 0 ? Math.round(posicoes.reduce((s, p) => s + (p.velocidade || 0), 0) / posicoes.length) : 0;

    return (
        <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'white', zIndex: 3000, display: 'flex', flexDirection: 'column', fontFamily: 'sans-serif'
        }}>
            {/* Header */}
            <div style={{
                background: '#0f3460', color: 'white', padding: '10px 20px',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0
            }}>
                <strong style={{ fontSize: 16 }}>📍 Histórico de Percurso</strong>
                <button onClick={onFechar} style={{
                    background: 'rgba(255,255,255,0.2)', color: 'white', border: 'none',
                    borderRadius: 6, padding: '6px 14px', cursor: 'pointer', fontSize: 13
                }}>✕ Fechar</button>
            </div>

            <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
                {/* Painel lateral */}
                <div style={{
                    width: 300, background: '#f8f9fa', borderRight: '1px solid #ddd',
                    padding: 16, overflowY: 'auto', flexShrink: 0
                }}>
                    <div style={{ marginBottom: 12 }}>
                        <label style={{ fontSize: 12, fontWeight: 600, color: '#444' }}>Veículo</label>
                        <select value={veiculoId} onChange={e => setVeiculoId(e.target.value)}
                            style={{ width: '100%', padding: '8px', borderRadius: 6, border: '1px solid #ddd', marginTop: 4, fontSize: 13 }}>
                            <option value="">Selecione...</option>
                            {veiculos.map(v => <option key={v.id} value={v.id}>{v.nome} — {v.placa}</option>)}
                        </select>
                    </div>

                    <div style={{ marginBottom: 12 }}>
                        <label style={{ fontSize: 12, fontWeight: 600, color: '#444' }}>Início</label>
                        <input type="datetime-local" value={inicio} onChange={e => setInicio(e.target.value)}
                            style={{ width: '100%', padding: '8px', borderRadius: 6, border: '1px solid #ddd', marginTop: 4, fontSize: 13, boxSizing: 'border-box' }} />
                    </div>

                    <div style={{ marginBottom: 16 }}>
                        <label style={{ fontSize: 12, fontWeight: 600, color: '#444' }}>Fim</label>
                        <input type="datetime-local" value={fim} onChange={e => setFim(e.target.value)}
                            style={{ width: '100%', padding: '8px', borderRadius: 6, border: '1px solid #ddd', marginTop: 4, fontSize: 13, boxSizing: 'border-box' }} />
                    </div>

                    <button onClick={buscar} disabled={carregando} style={{
                        width: '100%', padding: '10px', borderRadius: 6, border: 'none',
                        background: '#0f3460', color: 'white', fontWeight: 600, fontSize: 14, cursor: 'pointer'
                    }}>
                        {carregando ? 'Buscando...' : '🔍 Buscar'}
                    </button>

                    {erro && <div style={{ color: '#e74c3c', fontSize: 12, marginTop: 10 }}>{erro}</div>}

                    {posicoes.length > 0 && (
                        <>
                            <hr style={{ margin: '16px 0', borderColor: '#ddd' }} />
                            <div style={{ fontSize: 12, color: '#555', lineHeight: 2 }}>
                                <div>📍 <strong>{posicoes.length}</strong> posições</div>
                                <div>📏 <strong>{distancia()} km</strong> percorridos</div>
                                <div>🚀 Vel. máx: <strong>{velMax} km/h</strong></div>
                                <div>📊 Vel. média: <strong>{velMedia} km/h</strong></div>
                                <div>🕐 Início: <strong>{formatarData(posicoes[0].recebido_em)}</strong></div>
                                <div>🕐 Fim: <strong>{formatarData(posicoes[posicoes.length-1].recebido_em)}</strong></div>
                            </div>

                            <hr style={{ margin: '16px 0', borderColor: '#ddd' }} />
                            <div style={{ fontSize: 12, fontWeight: 600, color: '#444', marginBottom: 8 }}>
                                Velocidade do replay
                            </div>
                            <input type="range" min="1" max="20" value={velocidadeReplay}
                                onChange={e => setVelocidadeReplay(Number(e.target.value))}
                                style={{ width: '100%' }} />
                            <div style={{ fontSize: 11, color: '#888', textAlign: 'center' }}>{velocidadeReplay}x</div>

                            <button onClick={replay ? pararReplay : iniciarReplay} style={{
                                width: '100%', marginTop: 10, padding: '10px', borderRadius: 6, border: 'none',
                                background: replay ? '#e74c3c' : '#27ae60', color: 'white',
                                fontWeight: 600, fontSize: 14, cursor: 'pointer'
                            }}>
                                {replay ? '⏹ Parar Replay' : '▶ Iniciar Replay'}
                            </button>

                            {replay && posAtual && (
                                <div style={{ marginTop: 12, background: 'white', borderRadius: 8, padding: 10, fontSize: 12, lineHeight: 1.8 }}>
                                    <div>🕐 {formatarData(posAtual.recebido_em)}</div>
                                    <div>🚀 {posAtual.velocidade || 0} km/h</div>
                                    <div>🔑 {posAtual.ignicao ? 'Ligado' : 'Desligado'}</div>
                                    <div style={{ fontSize: 11, color: '#888' }}>
                                        {replayIdx + 1} / {posicoes.length}
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>

                {/* Mapa */}
                <div style={{ flex: 1 }}>
                    {isLoaded && (
                        <GoogleMap
                            mapContainerStyle={mapContainerStyle}
                            center={{ lat: -22.098, lng: -50.188 }}
                            zoom={13}
                            onLoad={onMapLoad}
                            options={{ fullscreenControl: false, streetViewControl: false, mapTypeControl: false }}
                        >
                            {/* Rota completa em cinza */}
                            {path.length > 1 && (
                                <Polyline path={path} options={{ strokeColor: '#cccccc', strokeWeight: 4, strokeOpacity: 0.6 }} />
                            )}

                            {/* Rota do replay em azul */}
                            {replay && pathReplay.length > 1 && (
                                <Polyline path={pathReplay} options={{ strokeColor: '#0f3460', strokeWeight: 5, strokeOpacity: 1 }} />
                            )}

                            {/* Ponto inicial */}
                            {posicoes.length > 0 && (
                                <Marker
                                    position={{ lat: posicoes[0].latitude, lng: posicoes[0].longitude }}
                                    label={{ text: 'A', color: 'white', fontWeight: 'bold' }}
                                    icon={{ url: 'https://maps.google.com/mapfiles/ms/icons/green-dot.png' }}
                                />
                            )}

                            {/* Ponto final */}
                            {posicoes.length > 1 && !replay && (
                                <Marker
                                    position={{ lat: posicoes[posicoes.length-1].latitude, lng: posicoes[posicoes.length-1].longitude }}
                                    label={{ text: 'B', color: 'white', fontWeight: 'bold' }}
                                    icon={{ url: 'https://maps.google.com/mapfiles/ms/icons/red-dot.png' }}
                                />
                            )}

                            {/* Marcador do replay */}
                            {replay && posAtual && (
                                <Marker
                                    position={{ lat: posAtual.latitude, lng: posAtual.longitude }}
                                    icon={{ url: 'https://maps.google.com/mapfiles/ms/icons/blue-dot.png' }}
                                />
                            )}
                        </GoogleMap>
                    )}
                </div>
            </div>
        </div>
    );
}
